import { useHttpClient, useToast } from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { Appointment, AppointmentsResponse } from './appointments.models';
import {
  appointmentsReducer,
  AppointmentsState,
  initState,
} from './appointments.reducer';

interface AppointmentsContextProps {
  appointments: Appointment[];
  currentAppointment: Appointment | undefined;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchAppointments: (options?: {
    signal?: AbortSignal;
  }) => Promise<AppointmentsResponse[]>;
  fetchAppointment: (
    appointmentId: string,
    options?: {
      signal?: AbortSignal;
      callback?: (appintment: Appointment) => Promise<unknown>;
    }
  ) => Promise<Appointment | void>;
  fetchAppointmentsSkipped: () => void;
  upsertAppointments: (appointments: Appointment[]) => void;
  removeAppointments: (appointmentIds: string[]) => void;
  setSelectedAppointmentId: (appointmentId: string) => void;
}

export const AppointmentsContext = createContext<
  AppointmentsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentsState>;
}>;

export const AppointmentsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(appointmentsReducer, {
    ...initState,
    ...initialState,
  });
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  const appointments = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .sort(
          (a, b) =>
            a.appointment.startDateTime.localeCompare(
              b.appointment.startDateTime
            ) ||
            a.appointment.endDateTime.localeCompare(
              b.appointment.endDateTime
            ) ||
            a.customer.name.localeCompare(b.customer.name)
        ),
    [state.entities, state.ids]
  );

  const currentAppointment = useMemo(
    () => state.entities[state.selectedId || ''],
    [state.entities, state.selectedId]
  );

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const fetchAppointments: AppointmentsContextProps['fetchAppointments'] =
    useCallback(
      async (options) => {
        dispatch({ type: 'FETCH_APPOINTMENTS' });

        try {
          const appointments = await httpClient.get<AppointmentsResponse[]>(
            `/appointments`,
            { signal: options?.signal }
          );
          dispatch({
            type: 'FETCH_APPOINTMENTS_SUCCESS',
            payload: { appointments },
          });
          return appointments;
        } catch (error) {
          if (error instanceof Error && error.message === 'canceled') {
            dispatch({ type: 'FETCH_APPOINTMENTS_CANCELLED' });
          } else {
            dispatch({
              type: 'FETCH_APPOINTMENTS_FAILURE',
              payload: { error: `${error}` },
            });
          }
          return [];
        }
      },
      [httpClient]
    );

  const fetchAppointment: AppointmentsContextProps['fetchAppointment'] =
    useCallback(
      async (appointmentId, options) => {
        dispatch({ type: 'FETCH_APPOINTMENT', payload: { appointmentId } });

        try {
          const appointment = await httpClient.get<Appointment>(
            `/appointments/${appointmentId}`,
            { signal: options?.signal }
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_SUCCESS',
            payload: { appointment },
          });

          options?.callback && (await options.callback(appointment));

          return appointment;
        } catch (error) {
          if (error instanceof Error && error.message === 'canceled') {
            dispatch({ type: 'FETCH_APPOINTMENT_CANCELLED' });
          } else {
            dispatch({
              type: 'FETCH_APPOINTMENT_FAILURE',
              payload: { error: `${error}` },
            });
          }
        }
      },
      [httpClient]
    );

  const fetchAppointmentsSkipped: AppointmentsContextProps['fetchAppointmentsSkipped'] =
    useCallback(() => {
      dispatch({ type: 'FETCH_APPOINTMENTS_SKIPPED' });
    }, []);

  const upsertAppointments: AppointmentsContextProps['upsertAppointments'] =
    useCallback((appointments) => {
      dispatch({
        type: 'UPSERT_APPOINTMENTS',
        payload: { appointments },
      });
    }, []);

  const removeAppointments: AppointmentsContextProps['removeAppointments'] =
    useCallback((appointmentIds) => {
      dispatch({
        type: 'REMOVE_APPOINTMENTS',
        payload: { appointmentIds },
      });
    }, []);

  const setSelectedAppointmentId: AppointmentsContextProps['setSelectedAppointmentId'] =
    useCallback((appointmentId) => {
      dispatch({
        type: 'SET_SELECTED_APPOINTMENT_ID',
        payload: { appointmentId },
      });
    }, []);

  return (
    <AppointmentsContext.Provider
      value={{
        fetchAppointments,
        fetchAppointment,
        fetchAppointmentsSkipped,
        upsertAppointments,
        removeAppointments,
        setSelectedAppointmentId,
        appointments,
        currentAppointment,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
      }}
    >
      {children}
    </AppointmentsContext.Provider>
  );
};

export const useAppointments = () => {
  const context = useContext(AppointmentsContext);
  if (context === undefined) {
    throw new Error(
      'useAppointments must be used within an AppointmentsProvider'
    );
  }
  return context;
};
